'use client'
import { useState, useEffect, useRef } from 'react'
import useHubSpotForm from '@/hooks/useHubSpotForm'
import useFormFunnelEvents from '@/hooks/useFormFunnelEvents'
import {
  FORM_FUNNEL_EVENTS,
  captureFormEvent,
  getVisibleFields,
  isTrustedHubSpotMessageOrigin,
} from '@/utils/formFunnelEvents'
import ConditionalJQueryLoader from '@/components/optimization/ConditionalJQueryLoader'

import { PrismicLink } from '@prismicio/react'
import type { GeoLocationData } from '@/types/geo'
import { getLangPath } from '@/utils/getLangFormat'
import posthog from 'posthog-js'
import { applyEnhancedUserData, getPendingEnhancedUserData, trackEvent, type EnhancedIdentifiers } from '@/utils/analytics'
import { CMMC_DEMO_FORM_UIDS } from '@/utils/cmmcLandingPages'
import { logger } from '@/utils/logger'

interface HubSpotFormMetaData {
  portalId?: string
  formId?: string
  target?: string
  sfdcCampaignId?: string
  [key: string]: unknown
}

interface HubSpotFormProps {
  uid?: string
  locale?: string
  type?: string
  formId?: string
  target?: string
  portalId?: string
  sfdcCampaignId?: string
  geoLocation?: GeoLocationData | null
  metaData?: HubSpotFormMetaData
  showBecomePartnerText?: boolean
  loadJQueryImmediately?: boolean
  // true loads the ChiliPiper concierge script at mount (legacy behavior);
  // the default defers it until HubSpot's onFormReady fires (or the visitor
  // touches the form, whichever comes first), keeping it off the page's
  // critical path while form shortening still completes before the form
  // is shown.
  loadChiliPiperImmediately?: boolean
  scrollOnSuccess?: boolean
  prefillMessage?: string
}

interface ChiliPiperConfig {
  formType: string
  formIds?: string[]
}

const getChiliPiperConfig = (uid?: string): ChiliPiperConfig => {
  if (uid === 'cmmc' || (uid && CMMC_DEMO_FORM_UIDS.includes(uid))) {
    return { formType: 'cmmc---demo-form-fill' }
  }
  switch (uid) {
    case 'referral-program':
      return { formType: 'secureframe-referral-form' }
    case 'rsa-demo':
      return { formType: 'event---demo-form-fill' }
    case 'fedramp-20x-phase-one-pilot':
      return { formType: 'hs---fedramp-form-fill' }
    default:
      return {
        formType: 'secureframe-discussion-form-fill',
        formIds: ['4cfd071b-f042-4fc3-8ae9-42f304a00a8e'],
      }
  }
}

// Label replacements for request-cmmc-demo form
const CMMC_LABEL_REPLACEMENTS: Record<string, string> = {
  // Who needs to handle CUI options
  'Not sure yet': 'Not sure',
  'A dedicated team (enclave)': 'Enclave',
  'Entire organization': 'Full organization',
  // CMMC Level options - remove parentheses
  'Level 1 (FCI only)': 'Level 1',
  'Level 2 (CUI)': 'Level 2',
  'Level 3 (DIBCAC)': 'Level 3',
}

const HubSpotForm = ({
  uid,
  locale,
  type,
  formId,
  portalId,
  sfdcCampaignId,
  geoLocation,
  metaData,
  target = 'hubspot-form',
  showBecomePartnerText,
  loadJQueryImmediately = true,
  loadChiliPiperImmediately = false,
  scrollOnSuccess = true,
  prefillMessage,
}: HubSpotFormProps) => {
  const [chiliPiperLoaded, setChiliPiperLoaded] = useState(false)
  const isCmmcDemoUid = Boolean(uid && CMMC_DEMO_FORM_UIDS.includes(uid))
  // CMMC hero forms stay invisible until the label rewrites below have run,
  // so visitors never see the raw HubSpot labels flash and then change.
  const [cmmcFormRevealed, setCmmcFormRevealed] = useState(!isCmmcDemoUid)
  const shouldEnableChiliPiper =
    (type === 'demoEmbeddedForm' || type === 'contactPartnerEmbeddedForm') && uid !== 'summit'

  useHubSpotForm(
    portalId,
    formId,
    sfdcCampaignId,
    type,
    geoLocation,
    metaData,
    target,
    chiliPiperLoaded,
    shouldEnableChiliPiper,
    uid,
    scrollOnSuccess,
    prefillMessage,
  )

  useFormFunnelEvents({ target, uid, form_id: formId, portal_id: portalId, form_type: type })

  // Keep the legal-consent rich text at the end of every embedded form
  // (MAR-5606). The portal form definitions place it after the email field,
  // and fields HubSpot renders later (progressive profiling, e.g. Phone
  // Number on resource downloads) otherwise appear below it, stranding
  // "By submitting..." in the middle of the form. Scoped to this instance's
  // container ref, and the consent block is identified structurally (the
  // rich text holding the privacy-policy link) so localized copy matches too.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const moveConsentLast = () => {
      const form = container.querySelector('form')
      if (!form) return
      const consent = Array.from(form.querySelectorAll('.hs-richtext')).find(
        (el) => el.querySelector('a[href*="privacy"]') || /by submitting/i.test(el.textContent ?? ''),
      )
      // No-op when already last, so the observer is never re-triggered by our own write.
      if (consent && form.lastElementChild !== consent) form.appendChild(consent)
    }

    moveConsentLast()
    const observer = new MutationObserver(moveConsentLast)
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  // Replace radio button labels and reorder elements for the CMMC demo form
  // (same HubSpot form GUID is reused across the CMMC landing pages)
  useEffect(() => {
    if (!uid || !CMMC_DEMO_FORM_UIDS.includes(uid)) return

    const modifyForm = () => {
      const formContainer = document.getElementById(target)
      if (!formContainer) return false

      // Replace radio button labels
      const radioLabels = formContainer.querySelectorAll('.hs-form-radio label span')
      let replacementsFound = false

      radioLabels.forEach((span) => {
        const text = span.textContent?.trim()
        if (text && CMMC_LABEL_REPLACEMENTS[text]) {
          span.textContent = CMMC_LABEL_REPLACEMENTS[text]
          replacementsFound = true
        }
      })

      // Move privacy policy text below submit button
      const form = formContainer.querySelector('form')
      const richText = formContainer.querySelector('.hs-richtext')
      const submitButton = formContainer.querySelector('.hs-submit')

      if (form && richText && submitButton && submitButton.parentNode === form && form.lastElementChild !== richText) {
        // Move richtext after submit button (skip when already in place so
        // this stays a no-op on repeat runs — the mutation observer below
        // must never be re-triggered by our own writes)
        form.appendChild(richText)
      }

      return replacementsFound
    }

    // HubSpot renders the full field set first, then collapses it to the
    // short progressive form, so the container is kept height-locked and
    // invisible until the DOM has been quiet for a beat. Rewrites run on
    // every mutation so the labels are final before anything is shown.
    const container = document.getElementById(target)
    const revealStartedAt = Date.now()
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    let revealed = false

    const reveal = (reason: 'settled' | 'hard_cap') => {
      if (revealed) return
      revealed = true
      observer.disconnect()
      clearTimeout(settleTimer)
      clearTimeout(hardCap)
      try {
        modifyForm()
      } catch (error) {
        logger.warn('[HubSpot] CMMC form final rewrite failed:', {
          error: error instanceof Error ? error.message : String(error),
        })
      }
      setCmmcFormRevealed(true)
      // A hard-cap reveal means the form was still mutating at 5s
      const revealedForm = container?.querySelector('form') ?? null
      captureFormEvent(FORM_FUNNEL_EVENTS.revealed, {
        uid,
        form_id: formId,
        portal_id: portalId,
        form_type: type,
        target,
        ms_waited: Date.now() - revealStartedAt,
        hit_hard_cap: reason === 'hard_cap',
        chilipiper_present: typeof window !== 'undefined' && Boolean(window.ChiliPiper),
        form_present: Boolean(revealedForm),
        field_count: revealedForm ? getVisibleFields(revealedForm).length : undefined,
      })
    }

    // ChiliPiper.setForm() collapses the full field set to the short form,
    // so when the concierge script is expected, hold the reveal until it has
    // arrived plus one extra settle cycle for setForm's rewrites (the 5s
    // hard cap below still bounds the wait) — visitors should never watch
    // fields disappear.
    let cpGraceGiven = false
    const scheduleReveal = () => {
      clearTimeout(settleTimer)
      settleTimer = setTimeout(() => {
        if (shouldEnableChiliPiper && !window.ChiliPiper) {
          scheduleReveal()
          return
        }
        if (shouldEnableChiliPiper && !cpGraceGiven) {
          cpGraceGiven = true
          scheduleReveal()
          return
        }
        reveal('settled')
      }, 500)
    }

    const observer = new MutationObserver(() => {
      if (revealed) return
      scheduleReveal()
    })
    if (container) {
      observer.observe(container, { childList: true, subtree: true })
    }
    scheduleReveal()

    // Never leave the form hidden if HubSpot loads slowly or not at all.
    const hardCap = setTimeout(() => reveal('hard_cap'), 5000)

    return () => {
      observer.disconnect()
      clearTimeout(settleTimer)
      clearTimeout(hardCap)
    }
  }, [uid, target, shouldEnableChiliPiper, formId, portalId, type])

  const getPartnerPath = (locale: string) => `/${getLangPath(locale)}/contact/partner`

  // Snapshot of the booking visitor's enhanced-conversion identifiers, captured
  // when the ChiliPiper widget opens (onRouted) so the deferred 'Meeting Booked'
  // conversion uses THIS submitter's email — not whatever a later form submit left
  // in the shared pending state (MAR-882 review).
  const containerRef = useRef<HTMLDivElement | null>(null)
  const bookingEnhancedIdsRef = useRef<EnhancedIdentifiers | null>(null)

  // A submission captured before concierge.js finished its deferred load,
  // queued so deployChiliPiper() can replay it through ChiliPiper.submit()
  // and the visitor still gets the calendar handoff.
  const pendingCpLeadRef = useRef<Record<string, string> | null>(null)
  const cpDeployedRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!shouldEnableChiliPiper) return

    const scriptId = 'chilipiper-concierge-script'
    const { formType, formIds } = getChiliPiperConfig(uid)
    let cancelled = false

    const deployChiliPiper = () => {
      const ChiliPiper = window.ChiliPiper
      if (!ChiliPiper || cancelled) return

      setChiliPiperLoaded(true)

      const callbacks = {
          onRouted: () => {
            try {
              // Snapshot the submitter's identifiers now — the widget just opened,
              // right after their form submit — so a later unrelated submit can't
              // change what the deferred Meeting Booked conversion sends (MAR-882).
              bookingEnhancedIdsRef.current = getPendingEnhancedUserData()
              posthog.capture('ChiliPiper Widget Opened', {
                form_type: type ?? 'unknown',
                chilipiper_router: formType,
              })
            } catch (error) {
              logger.error('[ChiliPiper] onRouted tracking failed:', {
                error: error instanceof Error ? error.message : String(error),
              })
            }
          },
          onSuccess: () => {
            try {
              // Apply the snapshot captured in onRouted so the enhanced conversion
              // carries THIS booking visitor's (normalized) email — never a later
              // visitor's (MAR-882 review).
              const ids = bookingEnhancedIdsRef.current
              applyEnhancedUserData(ids)
              trackEvent(
                'Meeting Booked',
                { form_type: type ?? 'unknown', chilipiper_router: formType },
                // Meta advanced matching can't be cleared once set; if this booking
                // captured no fresh identifiers, skip the Meta event so it can't attach
                // a previous visitor's advanced-matching (MAR-882 review).
                { skipMeta: !ids?.email && !ids?.phone },
              )
            } catch (error) {
              // Never disrupt ChiliPiper's post-booking flow, but do log it.
              logger.error('[ChiliPiper] Meeting Booked tracking failed:', {
                error: error instanceof Error ? error.message : String(error),
              })
            }
          },
          onClose: () => {
            try {
              posthog.capture('ChiliPiper Widget Closed', {
                form_type: type ?? 'unknown',
                chilipiper_router: formType,
              })
            } catch (error) {
              logger.error('[ChiliPiper] onClose tracking failed:', {
                error: error instanceof Error ? error.message : String(error),
              })
            }
          },
      }

      try {
        ChiliPiper.deploy('secureframe', formType, {
          formType: 'Hubspot',
          ...(formIds && { formIds }),
          ...callbacks,
        })
        cpDeployedRef.current = true
      } catch (error) {
        logger.error('[ChiliPiper] deploy failed:', { error: error instanceof Error ? error.message : String(error) })
        return
      }

      // Replay a submission that raced past the deferred script load
      // (captured by the submit listener below) so the visitor still gets
      // the calendar handoff.
      const pendingLead = pendingCpLeadRef.current
      if (pendingLead) {
        pendingCpLeadRef.current = null
        try {
          ChiliPiper.submit('secureframe', formType, { lead: pendingLead, ...callbacks })
        } catch (error) {
          logger.error('[ChiliPiper] queued submit replay failed:', {
            error: error instanceof Error ? error.message : String(error),
          })
        }
      }
    }

    if (window.ChiliPiper) {
      deployChiliPiper()
      return
    }

    // Deferred-load triggers. The concierge script is kept off the page's
    // critical path, but it must be in flight by the time the rendered form
    // is shown: ChiliPiper.setForm() (form shortening, called from
    // useHubSpotForm once window.ChiliPiper exists) is what collapses the
    // full field set to the short form, and it should finish inside the
    // height-locked window on the CMMC pages so visitors never watch fields
    // disappear. So: load on HubSpot's onFormReady message (primary), on the
    // visitor's first interaction with the container (fallback), and capture
    // any submit that still beats the script so deployChiliPiper() can
    // replay it via ChiliPiper.submit() and the calendar handoff is never
    // lost.
    let ensureScriptRequested = () => {}
    // The inline v2 embed posts hsFormCallback from our own window; iframed
    // variants post from HubSpot's form-hosting domains. Reject everything
    // else so an embedded third-party iframe can't forge a queued lead.
    const onHsFormSubmitMessage = (event: MessageEvent) => {
      if (!isTrustedHubSpotMessageOrigin(event.origin, window.location.origin)) return
      const data = event.data as
        | { type?: string; eventName?: string; id?: string; data?: unknown }
        | undefined
      if (!data || data.type !== 'hsFormCallback') return
      if (formId && data.id && data.id !== formId) return
      if (data.eventName === 'onFormReady') {
        ensureScriptRequested()
        return
      }
      if (data.eventName !== 'onFormSubmit') return
      if (cpDeployedRef.current) return
      const lead: Record<string, string> = {}
      if (Array.isArray(data.data)) {
        data.data.forEach((field) => {
          const f = field as { name?: string; value?: unknown }
          if (f?.name) lead[f.name] = String(f.value ?? '')
        })
      } else if (data.data && typeof data.data === 'object') {
        const values =
          (data.data as { submissionValues?: Record<string, unknown> }).submissionValues ??
          (data.data as Record<string, unknown>)
        Object.entries(values).forEach(([name, value]) => {
          lead[name] = String(value ?? '')
        })
      }
      pendingCpLeadRef.current = lead
      // Any interaction should already have started the load; make sure.
      ensureScriptRequested()
    }
    window.addEventListener('message', onHsFormSubmitMessage)

    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null
    if (existingScript) {
      const onLoad = () => deployChiliPiper()
      const onError = (error: Event) => {
        logger.error('[ChiliPiper] Script failed to load:', {
          error: error instanceof Error ? error.message : String(error),
          uid,
          target,
        })
      }
      existingScript.addEventListener('load', onLoad)
      existingScript.addEventListener('error', onError)
      return () => {
        cancelled = true
        existingScript.removeEventListener('load', onLoad)
        existingScript.removeEventListener('error', onError)
        window.removeEventListener('message', onHsFormSubmitMessage)
      }
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = 'https://secureframe.chilipiper.com/concierge-js/cjs/concierge.js'
    script.async = true

    const onLoad = () => deployChiliPiper()
    const onError = (error: Event) => {
      logger.error('[ChiliPiper] Script failed to load:', {
        error: error instanceof Error ? error.message : String(error),
        uid,
        target,
      })
    }

    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)

    let piggybackScript: HTMLScriptElement | null = null
    const appendScript = () => {
      const existing = document.getElementById(scriptId) as HTMLScriptElement | null
      if (!existing) {
        document.body.appendChild(script)
        return
      }
      if (existing !== script && !piggybackScript) {
        // Another HubSpotForm instance appended the shared script first;
        // ride its element so this instance still deploys (and replays any
        // queued submit) instead of orphaning its own load handler.
        piggybackScript = existing
        if (window.ChiliPiper) {
          deployChiliPiper()
        } else {
          existing.addEventListener('load', onLoad)
        }
      }
    }
    ensureScriptRequested = appendScript

    // Deferred load (default): the concierge script plus the booking-app
    // bundle it pulls (~230KB) stays out of the initial bandwidth fight and
    // loads once the HubSpot form has rendered (onFormReady above), with
    // first interaction as a fallback trigger; if a submit still beats the
    // script, the capture listener above queues it and deployChiliPiper()
    // replays it through ChiliPiper.submit().
    const container = document.getElementById(target)
    const armTarget = container ?? window
    const armEvents = container
      ? ['pointerenter', 'touchstart', 'focusin', 'keydown']
      : // Container not in the DOM (unexpected): fall back to loading once
        // the page has finished, preserving the old deferred behavior.
        ['load']
    const onFirstInteraction = () => {
      armEvents.forEach((e) => armTarget.removeEventListener(e, onFirstInteraction))
      appendScript()
    }

    if (loadChiliPiperImmediately) {
      appendScript()
    } else {
      armEvents.forEach((e) => armTarget.addEventListener(e, onFirstInteraction, { passive: true }))
    }

    return () => {
      cancelled = true
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
      piggybackScript?.removeEventListener('load', onLoad)
      armEvents.forEach((e) => armTarget.removeEventListener(e, onFirstInteraction))
      window.removeEventListener('message', onHsFormSubmitMessage)
    }
  }, [loadChiliPiperImmediately, target, type, uid, formId, shouldEnableChiliPiper])

  return (
    <>
      {/* Conditional jQuery loading based on form placement */}
      <ConditionalJQueryLoader immediate={loadJQueryImmediately} />

      <div
        ref={containerRef}
        id={target}
        // inert keeps the not-yet-revealed form out of the tab order, pointer
        // hit-testing, and the accessibility tree while HubSpot settles.
        inert={cmmcFormRevealed ? undefined : true}
        aria-hidden={cmmcFormRevealed ? undefined : true}
        className={`transition-opacity duration-300 ${target} ${type ?? ''} ${
          cmmcFormRevealed ? 'opacity-100' : 'h-[440px] overflow-hidden opacity-0'
        }`}
      />

      {showBecomePartnerText && (
        <div className="mt-6">
          <PrismicLink
            href={locale === 'en-us' ? '/contact/partner' : getPartnerPath(locale as string)}
            className="!text-primary-green-85">
            Become a Secureframe Partner or Reseller
          </PrismicLink>
        </div>
      )}
    </>
  )
}

export default HubSpotForm
